/**
 * Runs the theme's animations even for visitors whose OS asks for reduced
 * motion.
 *
 * Horizon gates most of its motion behind `@media (prefers-reduced-motion:
 * no-preference)`. On a machine with the OS accessibility setting turned on,
 * every one of those blocks is simply skipped -- which is why the Add to cart
 * button's tick never draws itself there: the four rules that animate its
 * check, tick, ring and line all live inside such a block, and the button
 * gets `data-added="true"` with a transition-duration of 0s.
 *
 * A stylesheet cannot cancel someone else's media query, so this walks the
 * loaded sheets, lifts the rules out of every `no-preference` block, and
 * re-inserts them with no condition attached. Eleven such blocks, forty
 * rules, at the time of writing -- but nothing here is hard-coded, so a theme
 * update that moves them keeps working.
 *
 * Three blocks do the opposite -- they switch things OFF under `reduce` --
 * and those cannot be lifted, only countered. They are handled by name at the
 * bottom. If a theme update adds a fourth, its effect will survive this file;
 * the console note says how many blocks were seen so a mismatch is visible.
 *
 * NOT DONE HERE: the theme also skips `document.startViewTransition` in JS
 * when reduced motion is set, so the cart's morph animations stay off. That
 * needs `matchMedia` patched before the theme's own scripts read it, i.e. in
 * the document head. It is deliberately left out for now: those view
 * transitions run on exactly the cart-drawer states involved in the drawer
 * closing itself, and that is not a variable to change while that bug is
 * still open.
 *
 * This overrides an accessibility preference the visitor set on purpose.
 * That is the ask, and it is a real trade: a small number of people enable
 * reduced motion because animation makes them ill.
 */
(function () {
  'use strict';

  var STYLE_ID = 'hookienest-force-animations';

  /* Counters for the three `reduce` blocks, which turn things off rather than
     on and so have to be reversed by hand rather than lifted. */
  var COUNTERS = [
    '.sticky-add-to-cart__bar, .accelerated-checkout-block {',
    '  transition-duration: var(--animation-speed, 0.2s) !important;',
    '}',
    'video-background-component video { display: revert !important; }',
    'slideshow-slides { scroll-behavior: smooth; }',
  ].join('\n');

  function collect(list, out, seen) {
    for (var i = 0; i < list.length; i++) {
      var rule = list[i];
      var cond = rule.conditionText ||
                 (rule.media && rule.media.mediaText) || '';

      if (rule.media && cond.indexOf('prefers-reduced-motion') !== -1) {
        // A `reduce` block is a switch-off; lifting it would apply the very
        // rules we are trying to escape.
        if (cond.indexOf('no-preference') !== -1) {
          seen.blocks++;
          for (var j = 0; j < rule.cssRules.length; j++) {
            out.push(rule.cssRules[j].cssText);
            seen.rules++;
          }
        }
        continue;
      }
      if (rule.cssRules) collect(rule.cssRules, out, seen);
    }
  }

  function apply() {
    var out = [];
    var seen = { blocks: 0, rules: 0 };

    for (var i = 0; i < document.styleSheets.length; i++) {
      var rules;
      try {
        rules = document.styleSheets[i].cssRules;
      } catch (e) {
        continue; // cross-origin sheet, not ours to read
      }
      if (rules) collect(rules, out, seen);
    }
    if (!seen.rules) return false;

    var previous = document.getElementById(STYLE_ID);
    if (previous) previous.remove();

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = out.join('\n') + '\n' + COUNTERS;
    document.head.appendChild(style);
    return true;
  }

  // Only worth doing for the visitors the gates actually exclude.
  if (!window.matchMedia ||
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  // Sheets can still be arriving; run once now and again at load so a
  // late stylesheet is not missed. apply() replaces its own output.
  apply();
  window.addEventListener('load', apply);
})();